import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api, useLang, useUnits, formatCurrency, formatApiError } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, Select, Section } from "../components/primitives";
import CatalogPicker from "../components/CatalogPicker";
import CreditNoteModal from "../components/CreditNoteModal";
import VatRatePicker from "../components/VatRatePicker";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft, FloppyDisk, X, FilePdf, FileXls, FileArchive, ArrowsClockwise, XCircle, FolderPlus } from "@phosphor-icons/react";

const emptyLine = { position_id: null, name: "", description: "", unit: "Stk", quantity: 1, unit_price: 0, discount_percent: 0 };

export default function InvoiceEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { units, byValue: unitByValue } = useUnits();
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get("type"); // "proforma" if URL has ?type=proforma

  const [positions, setPositions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [docType, setDocType] = useState(isNew && requestedType === "proforma" ? "proforma" : "invoice");
  const [stornoInvoiceNumber, setStornoInvoiceNumber] = useState("");
  const [reissueOfStornoNumber, setReissueOfStornoNumber] = useState("");
  const [sourceProformaNumber, setSourceProformaNumber] = useState("");
  const [form, setForm] = useState({
    number: "",
    title: "",
    customer_id: "",
    status: "draft",
    vat_rate: 19,
    vat_note: "",
    // Standard-Fußnote für neue Rechnungen — editierbar. Nicht für Proforma
    // (dort weniger sinnvoll) und wird beim Laden bestehender Rechnungen
    // sowieso überschrieben.
    notes: (isNew && requestedType !== "proforma") ? "Leistungszeitraum entspricht Rechnungsdatum" : "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    purchase_order_number: "",
    source_quote_number: "",
    source_aufmass_number: "",
    lines: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/positions"), api.get("/customers")]);
      setPositions(p.data); setCustomers(c.data);
      if (!isNew) {
        const { data } = await api.get(`/invoices/${id}`);
        setDocType(data.doc_type || "invoice");
        setStornoInvoiceNumber(data.storno_invoice_number || "");
        setReissueOfStornoNumber(data.reissue_of_storno_number || "");
        setSourceProformaNumber(data.source_proforma_number || "");
        setForm({
          number: data.number || "",
          title: data.title || "",
          customer_id: data.customer_id || "",
          status: data.status,
          vat_rate: data.vat_rate,
          vat_note: data.vat_note || "",
          notes: data.notes || "",
          invoice_date: data.invoice_date || "",
          due_date: data.due_date || "",
          purchase_order_number: data.purchase_order_number || "",
          source_quote_number: data.source_quote_number || "",
          source_aufmass_number: data.source_aufmass_number || "",
          lines: data.lines || [],
        });
      }
      setLoading(false);
    })();
  }, [id, isNew]);

  const totals = useMemo(() => {
    const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount_percent || 0) / 100), 0);
    const vat = subtotal * (Number(form.vat_rate || 0) / 100);
    return { subtotal, vat, total: subtotal + vat };
  }, [form.lines, form.vat_rate]);

  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const addEmpty = () => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] });

  const addFromCatalog = (pos) => {
    const qty = 1;
    setForm({
      ...form,
      lines: [...form.lines, { position_id: pos.id, name: pos.name, description: pos.description || "", unit: pos.unit || "Stk", quantity: qty, unit_price: pos.unit_price || 0, discount_percent: 0 }],
    });
    setPickerOpen(false);
  };

  const saveLineToCatalog = async (i) => {
    const l = form.lines[i];
    if (!l.name) return;
    try {
      const { data } = await api.post("/positions/from-free-line", {
        name: l.name, description: l.description || "", unit: l.unit || "Stk",
        quantity: 1, unit_price: Number(l.unit_price) || 0,
      });
      updateLine(i, { position_id: data.id });
      toast.success(`${data.number} → ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadAttachment = async (kind, filename) => {
    if (isNew) { toast.info(t("mustSaveFirst")); return; }
    try {
      const res = await api.get(`/invoices/${id}/${kind}`, { responseType: "blob", params: { lang } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const submitCreditNote = async (selection) => {
    try {
      const { data } = await api.post(`/invoices/${id}/credit-note`, selection);
      toast.success(lang === "de" ? "Gutschrift erstellt" : "Credit note created");
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        number: form.number || null,
        title: form.title,
        customer_id: form.customer_id || null,
        status: form.status,
        vat_rate: Number(form.vat_rate) || 0,
        vat_note: form.vat_note || "",
        notes: form.notes,
        invoice_date: form.invoice_date || null,
        due_date: form.due_date || null,
        source_quote_number: form.source_quote_number || null,
        source_aufmass_number: form.source_aufmass_number || null,
        purchase_order_number: form.purchase_order_number || "",
        lines: form.lines.map((l) => ({
          position_id: l.position_id, name: l.name, description: l.description || "",
          unit: l.unit || "Stk", quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0, discount_percent: Number(l.discount_percent) || 0,
        })),
      };
      if (isNew) {
        const url = docType === "proforma" ? "/invoices/proforma" : "/invoices";
        const { data } = await api.post(url, payload);
        navigate(`/invoices/${data.id}`);
      } else {
        const { data } = await api.put(`/invoices/${id}`, payload);
        setForm((f) => ({ ...f, number: data.number }));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const isProforma = docType === "proforma";
  const isStorno = docType === "storno";
  const isCreditNote = docType === "credit_note";
  // GoBD-Lock: Draft-Rechnungen sind frei editierbar. Sobald der Status
  // manuell auf non-draft gesetzt oder eine PDF heruntergeladen wurde
  // (Backend bumped dann auf 'issued'), greift der Lock.
  const isLocked = !isNew && !isProforma && form.status !== "draft";

  const createStorno = async () => {
    if (isNew) return;
    const ok = await confirm({
      title: lang === "de" ? "Storno-Rechnung erstellen?" : "Create cancellation invoice?",
      description: lang === "de"
        ? "Diese Rechnung wird storniert und es wird eine eigene Storno-Rechnung mit fortlaufender Nummer erzeugt. Die Original-Rechnung kann anschließend nicht mehr bearbeitet werden."
        : "This invoice will be cancelled and a separate storno invoice with its own consecutive number will be created. The original can no longer be edited afterwards.",
      confirmText: lang === "de" ? "Storno erstellen" : "Create storno",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoices/${id}/storno`);
      toast.success(lang === "de" ? `Storno ${data.number} erstellt` : `Storno ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const reissueFromStorno = async () => {
    if (isNew || !isStorno) return;
    const ok = await confirm({
      title: lang === "de" ? "Korrektur-Rechnung erstellen?" : "Create correction invoice?",
      description: lang === "de"
        ? "Es wird eine neue Rechnung mit der nächsten fortlaufenden Rechnungsnummer erstellt, die den Inhalt dieses Storno-Belegs wieder positiv übernimmt."
        : "A new invoice with the next consecutive number will be created, restoring the storno content as positive amounts.",
      confirmText: lang === "de" ? "Korrektur erstellen" : "Create correction",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoices/${id}/reissue`);
      toast.success(lang === "de" ? `Rechnung ${data.number} erstellt` : `Invoice ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const previewPdf = async () => {
    if (isNew) {
      toast.info(lang === "de" ? "Bitte zuerst speichern." : "Please save first.");
      return;
    }
    try {
      const res = await api.get(`/invoices/${id}/pdf`, {
        params: { lang, preview: true },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      // Best-effort revoke — after browser has loaded the URL
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const convertProformaToInvoice = async () => {
    if (isNew || !isProforma) return;
    const ok = await confirm({
      title: lang === "de" ? "Rechnung aus Proforma erstellen?" : "Create invoice from proforma?",
      description: lang === "de"
        ? "Es wird eine neue echte Rechnung mit fortlaufender Rechnungsnummer und einer 1:1-Kopie des Inhalts erstellt. Die Proforma-Rechnung bleibt unverändert bestehen."
        : "A new invoice with the next consecutive number and a 1:1 copy of the content will be created. The proforma stays intact.",
      confirmText: lang === "de" ? "Rechnung erstellen" : "Create invoice",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoices/${id}/convert-to-invoice`);
      toast.success(lang === "de" ? `Rechnung ${data.number} erstellt` : `Invoice ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">…</div>;

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)] hover:text-[var(--ink)]" data-testid="back-to-invoices">
          <ArrowLeft size={14} /> {t("invoices")}
        </Link>
      </div>

      <PageHeader
        eyebrow={isNew ? (docType === "proforma" ? "Proforma" : "New") : (isStorno ? (lang === "de" ? "Storno" : "Storno") : (isProforma ? "Proforma" : (isCreditNote ? "Gutschrift" : "Edit")))}
        title={isNew ? (docType === "proforma" ? t("newProforma") : t("newInvoice")) : (isStorno ? t("stornoInvoice") : (isProforma ? t("newProforma") : t("editInvoice")))}
        actions={
          <div className="flex gap-2 flex-wrap">
            {!isNew && (
              <>
                <Btn variant="secondary" onClick={previewPdf} data-testid="invoice-preview-pdf">
                  <FilePdf size={16} /> {lang === "de" ? "Vorschau" : "Preview"}
                </Btn>
                <Btn variant="secondary" onClick={() => downloadAttachment("pdf", `${form.number || id}.pdf`)} data-testid="invoice-download-pdf">
                  <FilePdf size={16} /> {t("downloadPdf")}
                </Btn>
                <Btn variant="secondary" onClick={() => downloadAttachment("zugferd", `${form.number || id}_ZUGFeRD.pdf`)} data-testid="invoice-download-zugferd">
                  <FileArchive size={16} /> {t("downloadZugferd")}
                </Btn>
                <Btn variant="secondary" onClick={() => downloadAttachment("xrechnung", `${form.number || id}_XRechnung.xml`)} data-testid="invoice-download-xrechnung">
                  <FileXls size={16} /> {t("downloadXrechnung")}
                </Btn>
                {isProforma && (
                  <Btn variant="secondary" onClick={convertProformaToInvoice} data-testid="proforma-to-invoice">
                    <ArrowsClockwise size={16} /> {lang === "de" ? "Rechnung erstellen" : "Create invoice"}
                  </Btn>
                )}
                {!isProforma && !isStorno && !isCreditNote && (
                  <Btn variant="secondary" onClick={() => setCreditNoteOpen(true)} data-testid="invoice-credit-note">
                    <ArrowsClockwise size={16} /> {lang === "de" ? "Gutschrift" : "Credit note"}
                  </Btn>
                )}
                {!isProforma && !isStorno && !isCreditNote && form.status !== "cancelled" && (
                  <Btn variant="danger" onClick={createStorno} data-testid="invoice-storno">
                    <XCircle size={16} /> {t("createStorno")}
                  </Btn>
                )}
                {isStorno && (
                  <Btn variant="secondary" onClick={reissueFromStorno} data-testid="invoice-reissue">
                    <ArrowsClockwise size={16} /> {t("reissueFromStorno")}
                  </Btn>
                )}
              </>
            )}
            <Btn onClick={save} disabled={saving} data-testid="invoice-save">
              <FloppyDisk size={16} /> {saving ? "…" : t("save")}
            </Btn>
          </div>
        }
      />

      {isLocked && (
        <div
          className="mb-4 text-sm text-[var(--muted)] border border-[var(--border)] bg-[var(--row-hover)] px-3 py-2 rounded-sm flex items-center gap-2"
          data-testid="invoice-lock-banner"
        >
          <XCircle size={16} className="text-[var(--brand)]" />
          <span>{t("invoiceLocked")}</span>
        </div>
      )}

      {error && <div className="mb-4 text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title={t("details")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("number")}>
                <TextInput data-testid="invoice-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="R-2026-0001" readOnly={isLocked} />
              </Field>
              <Field label={t("title")}>
                <TextInput data-testid="invoice-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} readOnly={isLocked} />
              </Field>
              <Field label={t("customer")}>
                <Select data-testid="invoice-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} disabled={isLocked}>
                  <option value="">— {t("noCustomer")} —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company || c.name || "—"}{c.company && c.name ? ` · ${c.name}` : ""}</option>)}
                </Select>
              </Field>
              <Field label={t("status")}>
                <Select data-testid="invoice-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">{t("draft")}</option>
                  <option value="issued">{t("issued")}</option>
                  <option value="paid">{t("paid")}</option>
                  <option value="overdue">{t("overdue")}</option>
                  <option value="cancelled">{t("cancelled")}</option>
                </Select>
              </Field>
              <Field label={t("invoiceDate")}>
                <TextInput type="date" data-testid="invoice-date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} readOnly={isLocked} />
              </Field>
              <Field label={t("dueDate")}>
                <TextInput type="date" data-testid="invoice-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </Field>
              <Field label={t("purchaseOrderNumber")}>
                <TextInput
                  data-testid="invoice-po-number"
                  value={form.purchase_order_number}
                  onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
                  placeholder={lang === "de" ? "z. B. BST-2026-001" : "e.g. PO-2026-001"}
                />
              </Field>
              <Field label={t("vatRate")}>
                <VatRatePicker
                  value={form.vat_rate}
                  note={form.vat_note}
                  onChange={(rate, note) => setForm({ ...form, vat_rate: rate, vat_note: note })}
                  disabled={isLocked}
                />
              </Field>
              {form.source_quote_number && (
                <Field label={t("sourceQuote")}>
                  <TextInput value={form.source_quote_number} readOnly data-testid="invoice-source-quote" />
                </Field>
              )}
              {form.source_aufmass_number && (
                <Field label={lang === "de" ? "Aufmaß" : "Measurement"}>
                  <TextInput value={form.source_aufmass_number} readOnly data-testid="invoice-source-aufmass" />
                </Field>
              )}
              {stornoInvoiceNumber && (
                <Field label={lang === "de" ? "Storno zu" : "Cancels"}>
                  <TextInput value={stornoInvoiceNumber} readOnly data-testid="invoice-storno-ref" />
                </Field>
              )}
              {reissueOfStornoNumber && (
                <Field label={lang === "de" ? "ersetzt Storno" : "replaces storno"}>
                  <TextInput value={reissueOfStornoNumber} readOnly data-testid="invoice-reissue-ref" />
                </Field>
              )}
              {sourceProformaNumber && (
                <Field label={lang === "de" ? "aus Proforma" : "from proforma"}>
                  <TextInput value={sourceProformaNumber} readOnly data-testid="invoice-source-proforma" />
                </Field>
              )}
              <Field label={t("notes")} className="md:col-span-2">
                <TextArea data-testid="invoice-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
          </Section>

          <Section
            title={t("positions")}
            right={
              !isLocked ? (
                <div className="flex gap-2">
                  <Btn variant="secondary" onClick={() => setPickerOpen(true)} data-testid="pick-position-btn"><Plus size={14} /> {t("pickPosition")}</Btn>
                  <Btn variant="secondary" onClick={addEmpty} data-testid="add-empty-line-btn"><Plus size={14} /> {t("emptyLine")}</Btn>
                </div>
              ) : null
            }
          >
            {form.lines.length === 0 ? (
              <div className="text-sm text-[var(--muted-2)] py-6 text-center">
                {lang === "de" ? "Noch keine Positionen auf der Rechnung." : "No lines yet."}
              </div>
            ) : (
              <div className={`overflow-x-auto ${isLocked ? "opacity-80 pointer-events-none" : ""}`}>
                <table className="w-full text-sm">
                  <thead className="text-[var(--muted)]">
                    <tr className="border-b border-[var(--border)]">
                      <th className="text-left py-2 label-overline pr-2">{t("description")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-20">{t("quantity")}</th>
                      <th className="text-left py-2 label-overline pr-2 w-24">{t("unit")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-28">{t("unitPrice")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-20">{t("discount")} %</th>
                      <th className="text-right py-2 label-overline pr-2 w-28">{t("lineTotal")}</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, i) => {
                      const lineTotal = Number(l.quantity || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount_percent || 0) / 100);
                      const unitInfo = unitByValue[l.unit] || {};
                      const integerOnly = unitInfo.integer_only;
                      return (
                        <tr key={i} className="border-b border-[var(--border)] align-top">
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`inv-line-name-${i}`} value={l.name} onChange={(e) => updateLine(i, { name: e.target.value })} className="mb-1" placeholder={t("positionName")} />
                            <TextInput data-testid={`inv-line-desc-${i}`} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder={t("description")} />
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput
                              data-testid={`inv-line-qty-${i}`}
                              type="number"
                              step={integerOnly ? "1" : "0.01"}
                              min="0"
                              className="text-right"
                              value={l.quantity}
                              onChange={(e) => {
                                const v = e.target.value;
                                updateLine(i, { quantity: integerOnly && v !== "" ? String(parseInt(v, 10) || 0) : v });
                              }}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Select data-testid={`inv-line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}>
                              {units.map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
                            </Select>
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`inv-line-price-${i}`} type="number" step="0.01" className="text-right" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`inv-line-discount-${i}`} type="number" step="0.1" className="text-right" value={l.discount_percent} onChange={(e) => updateLine(i, { discount_percent: e.target.value })} />
                          </td>
                          <td className="py-2 pr-2 text-right font-medium whitespace-nowrap">
                            {formatCurrency(lineTotal, lang)}
                          </td>
                          <td className="py-2">
                            <div className="flex gap-1 mt-1">
                              {!l.position_id && l.name && (
                                <button type="button" onClick={() => saveLineToCatalog(i)} data-testid={`inv-line-to-catalog-${i}`}
                                  className="text-[var(--muted)] hover:text-[var(--brand)]" title={lang === "de" ? "Zum Katalog hinzufügen" : "Add to catalog"}>
                                  <FolderPlus size={14} />
                                </button>
                              )}
                              <button type="button" onClick={() => removeLine(i)} data-testid={`inv-line-remove-${i}`} className="text-[var(--muted-2)] hover:text-[#FF3B30]">
                                <Trash size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          <div className="swiss-card p-6 sticky top-6">
            <div className="label-overline mb-4">{t("total")}</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--muted)]">{t("subtotal")}</span>
                <span className="font-mono">{formatCurrency(totals.subtotal, lang)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--muted)]">{t("vat")} ({form.vat_rate}%)</span>
                <span className="font-mono">{formatCurrency(totals.vat, lang)}</span>
              </div>
              <div className="h-px bg-[var(--border)] my-2" />
              <div className="flex justify-between items-baseline">
                <span className="font-heading text-base font-medium">{t("total")}</span>
                <span className="font-heading text-2xl font-semibold tracking-tight text-[var(--brand)]" data-testid="invoice-total">
                  {formatCurrency(totals.total, lang)}
                </span>
              </div>
            </div>
            <div className="mt-6">
              <Btn onClick={save} disabled={saving} className="w-full h-11" data-testid="invoice-save-side">
                <FloppyDisk size={16} /> {saving ? "…" : t("save")}
              </Btn>
            </div>
          </div>
        </div>
      </div>

      <CatalogPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addFromCatalog} />
      <CreditNoteModal
        open={creditNoteOpen}
        onClose={() => setCreditNoteOpen(false)}
        invoice={{ ...form, id }}
        onSubmit={submitCreditNote}
        lang={lang}
      />
    </div>
  );
}
